import React, { useState, Fragment, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { notification, Radio } from 'antd';
import { getPricing, getPlanDetails, features, getPlanName, getUserTypeA, displayAssociations, getIntervalNumber, isValidated } from './plans';
import { SubsContext } from './SubscriptionInfo';
import CancellationModal from './CancellationModal';
import displayHyperlinks from '../grids/ItemDescInfoGrid/displayHyperlinks';

const TableContainer = styled.div`
  margin: 32px 0;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const TableWrapper = styled.div`
  position: relative;
  background: white;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #E5E7EB;
`;

const ScrollableSection = styled.div`
  overflow-y: auto;
  max-height: calc(100vh - 450px);
  border-top: 1px solid #E5E7EB;
  border-bottom: 1px solid #E5E7EB;

  /* Styling for Chrome, Safari, and newer Edge */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #F3F4F6;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #D1D5DB;
    border-radius: 4px;
    
    &:hover {
      background: #9CA3AF;
    }
  }

  /* Styling for Firefox */
  scrollbar-width: thin;
  scrollbar-color: #D1D5DB #F3F4F6;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  table-layout: fixed;
`;

const HeaderTable = styled(Table)`
  border-bottom: none;
`;

const FeatureTable = styled(Table)`
  border: none;
`;

const FooterTable = styled(Table)`
  border-top: none;
`;

const Th = styled.th`
  padding: 16px;
  text-align: center;
  font-weight: 600;
  color: #374151;
  background: #F9FAFB;
  border: 1px solid #E5E7EB;
  width: ${props => props.isFirst ? '250px' : 'auto'};
  
  &:first-child {
    text-align: left;
  }
`;

const Td = styled.td`
  padding: 16px;
  text-align: center;
  color: #4B5563;
  border: 1px solid #E5E7EB;
  width: ${props => props.isFirst ? '250px' : 'auto'};
  font-weight: 600;
  
  &:first-child {
    text-align: left;
    font-weight: 700;
  }
`;

const PriceCell = styled(Td)`
  font-size: 20px;
  font-weight: bold;
  color: #374151;
`;

const CheckMark = styled.span`
  color: #28a745;
  font-size: 20px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
`;

const SubscribeButton = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.2s ease;
  width: 90%;
  max-width: 200px;
  cursor: pointer;
  
  ${props => props.disabled ? `
    background-color: #E5E7EB;
    color: #9CA3AF;
    cursor: not-allowed;
  ` : `
    background-color: #3B82F6;
    color: white;
    border: none;
    
    &:hover {
      background-color: #2563EB;
    }
    
    &:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.5);
    }
  `}
`;

const CancelButton = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.2s ease;
  width: 90%;
  max-width: 200px;
  cursor: pointer;
  background-color: #dc3545;
  color: white;
  border: none;
  
  ${props => props.disabled ? `
    background-color: #E5E7EB;
    color: #9CA3AF;
    cursor: not-allowed;
  ` : `
    background-color: #ef4136;
    color: white;
    border: none;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(239, 65, 54, 0.5);
  }
`};

`;

const Note = styled.div`
  text-align: center;
  margin-top: 16px;
  color: #374151;
  font-weight: 600;
  font-size: 1rem;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  flex-direction: column;
  color: white;
  font-size: 18px;
`;

const SpinnerContainer = styled.div`
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const getBasePrice = (price) => {
  if (price === 'FREE' || price === 'n/a') return 0;
  return parseInt(price.replace('$', '').replace('/month', ''), 10);
};

const PlanTable = ({ onSubscribe, isLoading }) => {
  const {
    plans,
    intervals,
    billsbyCustomerId,
    subscription,
    associationValidations,
    subscriptionLevel,
    previousPlan,
  } = useContext(SubsContext);
  const selectedUserType = getUserTypeA(associationValidations);
  const [interval, setInterval] = useState(0);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [resubscribeLoading, setResubscribeLoading] = useState(false);
  const [isCancellingSubscription, setIsCancellingSubscription] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [isLoadingAPI, setIsLoadingAPI] = useState(false);

  const currSubscriptionLevel = subscription ? subscription.planName.split(' - ')[1] : subscriptionLevel;
  const nextSubscriptionLevel = subscription && subscription.planIdNext ? getPlanName(subscription.planIdNext, plans).split(' - ')[1] : currSubscriptionLevel;

  // Keep track of loading state changes for debugging
  useEffect(() => {
    console.log('Loading states changed:', {
      isCancellingSubscription,
      resubscribeLoading,
      isPaymentProcessing,
      isLoadingAPI
    });
  }, [isCancellingSubscription, resubscribeLoading, isPaymentProcessing, isLoadingAPI]);

  useEffect(() => {
    setInterval(getIntervalNumber(intervals, plans, subscription));
  }, [subscription]);

  // Update isLoadingAPI whenever any of the individual loading states change
  useEffect(() => {
    const isAnyLoading = isCancellingSubscription || resubscribeLoading || isPaymentProcessing;
    setIsLoadingAPI(isAnyLoading);

    // If we're starting a loading operation, set a safety timeout
    if (isAnyLoading) {
      console.log('Starting loading operation with timeout');

      // Create a safety timeout that will clear loading states after 15 seconds
      const safetyTimeout = setTimeout(() => {
        console.warn('Safety timeout triggered - clearing loading states');
        setIsCancellingSubscription(false);
        setResubscribeLoading(false);
        setIsPaymentProcessing(false);
        setIsLoadingAPI(false);

        // Show notification to user
        notification.warning({
          message: 'Operation Timeout',
          description: 'The operation is taking longer than expected. Please refresh the page and try again.',
          duration: 5
        });
      }, 15000); // 15 second timeout - reduced from 30 seconds

      // Clear timeout when loading states change
      return () => {
        console.log('Clearing timeout due to loading state change');
        clearTimeout(safetyTimeout);
      };
    }

    // Return empty cleanup function when no loading is happening
    return () => { };
  }, [isCancellingSubscription, resubscribeLoading, isPaymentProcessing]);

  const handleSubscribeClick = (planName, myPreviousPlan = '') => {
    const pricing = getPricing(selectedUserType, plans, intervals[interval]);
    const price = pricing[planName];
    const plan = getPlanDetails(selectedUserType, planName, plans, intervals[interval]);

    onSubscribe({
      ...plan,
      userType: selectedUserType,
      basePrice: getBasePrice(price),
      price
    }, myPreviousPlan || '');
  };

  const handleUpdateCard = () => {
    handleSubscribeClick(nextSubscriptionLevel);
  }

  const handleCancelSubscription = () => {
    if (previousPlan) {
      handleSubscribeClick(previousPlan);
    } else {
      console.log('Cancel subscription clicked - opening modal');
      setIsCancelModalOpen(true);
    }
  };

  const handleConfirmCancellation = async ({ reason, feedback }) => {
    // Determine new subscription level based on industry association
    const newPlanName = getUserTypeA(associationValidations) === 'Associated' ? 'Basic' : 'None';
    handleSubscribeClick(newPlanName, nextSubscriptionLevel)
    setIsCancelModalOpen(false);

    // TODO - We will want to send feedback to NFFS for cancellation eventually.
    if (reason || feedback) {
      console.log('Would have stored downgrade reason (skipped):', {
        customerUniqueId: billsbyCustomerId,
        reason,
        feedback
      });
    }
  };

  const isSubscriptionDisabled = (planName) => {
    if (!subscription || subscription.status === 'Cancelled' || subscription.status === 'Suspended') {
      return false
    }
    const affiliation = subscription.planName.split(' - ')[0];
    const [nextAffiliation, myNextSubscriptionLevel] = (subscription.planIdNext ?
      getPlanName(subscription.planIdNext, plans) :
      subscription.planName).split(' - ');

    const nextIntervalNumber = getIntervalNumber(intervals, plans, subscription);
    return planName === myNextSubscriptionLevel && nextIntervalNumber === interval && affiliation === nextAffiliation;
  };

  return (
    <div>
      {!(!subscription && billsbyCustomerId) && (
        <Fragment>
          {/* Loading overlay for all API operations */}
          {isLoadingAPI && (
            <LoadingOverlay data-testid="loading-overlay">
              <SpinnerContainer />
              <div>Processing...</div>
              <div style={{ fontSize: '14px', marginTop: '10px' }}>Please wait, this may take a moment</div>
            </LoadingOverlay>
          )}

          {/* Main content */}
          <TableContainer>
            <TableWrapper>
              {/* Header section with column titles */}
              <HeaderTable>
                <colgroup>
                  <col style={{ width: '250px' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '20%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <Th isFirst>Features</Th>
                    <Th>None</Th>
                    <Th>Basic</Th>
                    <Th>Advanced</Th>
                    <Th>Full</Th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <Td isFirst>
                      <b>
                        {intervals.length !== 1 && (
                          <Radio.Group value={interval} onChange={() => setInterval((interval + 1) % intervals.length)}>
                            {intervals.map((iVal, i) =>
                              (<span><Radio value={i}>{iVal} Price</Radio><br /></span>))}
                          </Radio.Group>
                        )}
                        {intervals.length === 1 && (
                          <span>{intervals[0]} Price</span>
                        )}
                      </b>
                    </Td>
                    <PriceCell>{getPricing(selectedUserType, plans, intervals[interval]).None}</PriceCell>
                    <PriceCell>{getPricing(selectedUserType, plans, intervals[interval]).Basic}</PriceCell>
                    <PriceCell>{getPricing(selectedUserType, plans, intervals[interval]).Advanced}</PriceCell>
                    <PriceCell>{getPricing(selectedUserType, plans, intervals[interval]).Full}</PriceCell>
                  </tr>
                </tbody>
              </HeaderTable>

              {/* Scrollable features section */}
              <ScrollableSection>
                <FeatureTable>
                  <colgroup>
                    <col style={{ width: '250px' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '20%' }} />
                  </colgroup>
                  <tbody>
                    {features.map((feature) => (
                      <tr key={feature}>
                        <Td isFirst>{displayHyperlinks(feature)}</Td>
                        <Td>{getPlanDetails(selectedUserType, 'None', plans, intervals[interval]).features.includes(feature) && <CheckMark>✓</CheckMark>}</Td>
                        <Td>{getPlanDetails(selectedUserType, 'Basic', plans, intervals[interval]).features.includes(feature) && <CheckMark>✓</CheckMark>}</Td>
                        <Td>{getPlanDetails(selectedUserType, 'Advanced', plans, intervals[interval]).features.includes(feature) && <CheckMark>✓</CheckMark>}</Td>
                        <Td>{getPlanDetails(selectedUserType, 'Full', plans, intervals[interval]).features.includes(feature) && <CheckMark>✓</CheckMark>}</Td>
                      </tr>
                    ))}
                  </tbody>
                </FeatureTable>
              </ScrollableSection>

              {/* Footer section with subscribe buttons */}
              <FooterTable>
                <colgroup>
                  <col style={{ width: '250px' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '20%' }} />
                </colgroup>
                <tbody>
                  <tr>
                    <Td isFirst>
                      {billsbyCustomerId ? (
                        <ButtonContainer>
                          <SubscribeButton
                            onClick={handleUpdateCard} className="update-card-btn"
                            disabled={!!previousPlan}
                          >
                            Update Card
                          </SubscribeButton>
                          <CancelButton
                            onClick={handleCancelSubscription}
                            className="cancel-subscription-btn"
                          >
                            {previousPlan ? "Reinstate" : "Cancel"} Subscription
                          </CancelButton>
                        </ButtonContainer>
                      ) : (
                        <span>Subscribe</span>
                      )}
                    </Td>
                    {['None', 'Basic', 'Advanced', 'Full'].map(planName => (
                      <Td key={planName}>
                        <SubscribeButton
                          onClick={() => handleSubscribeClick(planName)}
                          disabled={isSubscriptionDisabled(planName) || isLoading}
                        >
                          {isLoading ? 'Processing...' : `Subscribe to ${planName}`}
                        </SubscribeButton>
                      </Td>
                    ))}
                  </tr>
                </tbody>
              </FooterTable>
            </TableWrapper>
            <Note>
              Industrial Affiliations: {displayAssociations(associationValidations)}
            </Note>
            {isValidated(associationValidations) && (
              <Note>
                You have a discount on your subscription since you have at least one validated Industrial Affiliation
              </Note>
            )}
            {!isValidated(associationValidations) && (
              <Note>
                You can obtain a discount on your subscription if you have at least one validated Industrial Affiliation
              </Note>
            )}
          </TableContainer>
          <CancellationModal
            isOpen={isCancelModalOpen}
            onClose={() => setIsCancelModalOpen(false)}
            onConfirm={handleConfirmCancellation}
          />
        </Fragment >
      )}
    </div>
  );
};

PlanTable.propTypes = {
  onSubscribe: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,

};

export default PlanTable;



// WEBPACK FOOTER //
// ./src/components/Billsby/PlanTable.jsx